import { styled } from '@mui/material'
import { useAvailableOrganisations } from '@omnea-app/fe-auth/useAvailableOrganisations'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { LoggedInUserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import { containsOnlyOne } from '@omnea-app/packages/universal/general/arrays'
import { MultipleOrganisationPicker } from './MultipleOrganisationPicker'
import { OrganisationLogo } from './OrganisationLogo'

export type OrganisationIndicatorVariant = 'bar' | 'pill'

type OrganisationIndicatorProps = {
  variant?: OrganisationIndicatorVariant
}

export const OrganisationIndicator: React.FC<OrganisationIndicatorProps> = ({ variant = 'bar' }) => {
  const { data, isLoading } = useAvailableOrganisations()
  const { availableOrganisations } = data || { availableOrganisations: [] }
  const Container = wrapperFor(variant)

  if (isLoading) return <Container sx={{ width: 150 }} />

  const hasMultipleOrgs = availableOrganisations.length > 1

  if (hasMultipleOrgs) {
    return <MultipleOrganisationPicker availableOrganisations={availableOrganisations} variant={variant} />
  }

  if (!containsOnlyOne(availableOrganisations)) return null

  // If there is only one organisation, we don't need to show the picker
  return (
    <Container>
      <OrganisationLogo organisation={availableOrganisations[0]} />
      {variant === 'pill' && <CurrentUserAvatar />}
    </Container>
  )
}

export const CurrentUserAvatar: React.FC = () => {
  const userData = useAuthenticatedUser()

  if (!userData.isFullyAuthenticated || !userData.user) return null

  return <LoggedInUserAvatar user={userData.user} />
}

export const wrapperFor = (variant: OrganisationIndicatorVariant) => (variant === 'pill' ? PillWrapper : Wrapper)

export const Wrapper = styled('div')(({ theme }) => ({
  height: 50,
  padding: `${theme.spacing(3)} ${theme.spacing(6)}`,
  border: 'none',
  borderLeft: theme.borders.stroke.default,
  borderColor: theme.palette.border.subtle,
  display: 'grid',
  alignItems: 'center',
}))

export const PillWrapper = styled('div')(({ theme }) => ({
  height: 36,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(5)}`,
  border: `${theme.strokeWidth.default} solid ${theme.palette.border.strong}`,
  borderRadius: theme.shape['radius-full'],
  backgroundColor: theme.palette.bg.surface.default,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
}))
